import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom"
import MainLayout from "@/pages/layouts/MainLayout"
import CustomerList from "@/pages/customers/CustomerList"
import Login from "./pages/authentication/Login"
import BranchList from "@/pages/branches/BranchList"
import CashDenominationList from "@/pages/cash-denominations/CashDenominationList"
import SupplierList from "@/pages/suppliers/SupplierList"
import ProductList from "@/pages/products/ProductList"
import BranchProductList from "@/pages/products/BranchProductList"
import ProductVariantList from "@/pages/variant-product/ProductVariantList"
import AttributeList from "@/pages/attributes/AttributeList"
import AttributeDetails from "@/pages/attributes/AttributeDetails"
import TagList from "@/pages/tags/TagList"
import OrderList from "@/pages/orders/OrderList"
import UserList from "@/pages/users/UserList"
import AuthRoutes from "./guards/AuthRoutes"
import GuestRoutes from "./guards/GuestRoutes"
import NotFound from "./pages/not-found"
import Logout from "./pages/authentication/Logout"
import UserDetails from "./pages/users/UserDetails"
import BranchDetails from "./pages/branches/BranchDetails"
import CustomerDetails from "./pages/customers/CustomerDetails"
import SupplierDetails from "@/pages/suppliers/SupplierDetails"
import TagDetails from "@/pages/tags/TagDetails"
import RoleList from "./pages/role/RoleList"
import StockTransactionList from "@/pages/stockTransaction/StockTransactionList"
import StockAuditList from "@/pages/stock-audit/StockAuditList"
import StockAdjustmentList from "@/pages/stock-adjustment/StockAdjustmentList"
import DamageProductsList from "@/pages/stockTransaction/DamageProductsList"
import OrderDetails from "./pages/orders/OrderDetails"
import VariantDetails from "./pages/variant-product/VariantDetails"
import ProductDetails from "./pages/products/ProductDetails"
import InvoiceDetails from "./pages/invoices/InvoiceDetails"
import AdjustmentDetails from "@/pages/stock-adjustment/AdjustmentDetails"
import Dashboard from "./pages/Dashboard"
import CompanyDashboard from "./pages/company-admin/CompanyDashboard"
import { isAdminPortal } from "./utilities/domain"
import ActivityLogs from "./pages/system-logs/ActivityLogsList"
import PurchaseList from "./pages/purchase/PurchaseList"
import PaymentMethodsList from "./pages/payment-methods/PaymentMethodsList"
import ReasonList from "@/pages/reasons/ReasonList"
import SalesDashboard from "./pages/reports/SalesDashboard"
import PurchaseDetails from "./pages/purchase/PurchaseDetails"
import CustomerReturnList from "./pages/customer-returns/CustomerReturnList"
import AttributeNameList from "./pages/attribute-name/AttributeNameList"
import ProductDashboard from "./pages/reports/ProductDashboard"
import InventoryConfigurationPage from "./pages/inventory/InventoryConfigurationPage"
import SalesReturnList from "./pages/sales-return/SalesReturnList"
import ReturnRequestList from "./pages/return-requests/ReturnRequestList"
import CategoryList from "./pages/categories/CategoryList"
import CategoryDetails from "./pages/categories/CategoryDetails"
import { websiteRoutes } from "./pages/website/router"
import WebsiteOrderList from "./pages/website-orders/WebsiteOrderList"
import WebsiteOrderDetails from "./pages/website-orders/WebsiteOrderDetails"
import BrandList from "./pages/brand/BrandList"
import BrandDetails from "./pages/brand/BrandDetails"
import LocationList from "./pages/locations/LocationList"
import DeliveryFeeList from "./pages/delivery-fees/DeliveryFeeList"
import DenominationSettingList from "./pages/denomination-setting/DenominationSettingList"
import CurrencyNoteList from "./pages/currency-note-setting/CurrencyNoteList"
import ExpenseList from "./pages/expense/ExpenseList"
import CashDenominationDetails from "./pages/cash-denominations/CashDenominationDetails"
import WebsiteConfig from "./pages/website-config/WebsiteConfig"
import SalesStatusList from "./pages/payment-methods/SalesStatusList"
import PaymentStatusList from "./pages/payment-methods/PaymentStatusList"
import POSConfig from "./pages/pos-config/POSConfig"
import StockAuditDetailPage from "./pages/stock-audit/StockAuditDetail"
import DiscountList from "./pages/discount/DiscountList"
import DiscountDetail from "./pages/discount/DiscountDetail"
import ExportList from "./pages/exports/ExportList"
import StaffPage from "./pages/hr/StaffPage"
import AttendancePage from "./pages/hr/AttendancePage"
import IncentivesPage from "./pages/hr/IncentivesPage"

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      {/* Website Routes */}
      {websiteRoutes}

      <Route element={<GuestRoutes />}>
        <Route path="/pos/login" element={<Login />} />
      </Route>

      {/* auth routes */}
      <Route element={<AuthRoutes />}>
        <Route path="/pos/logout" element={<Logout />} />

        <Route path="/pos" element={<MainLayout />}>
          <Route index element={<Navigate to="/pos/dashboard" replace />} />
          <Route
            path="/pos/dashboard"
            element={isAdminPortal() ? <CompanyDashboard /> : <Dashboard />}
          />
          <Route path="/pos/users" element={<UserList />} />
          <Route path="/pos/user/:id" element={<UserDetails />} />
          <Route path="/pos/customers" element={<CustomerList />} />
          <Route path="/pos/staff" element={<StaffPage />} />
          <Route path="/pos/attendance" element={<AttendancePage />} />
          <Route path="/pos/incentives" element={<IncentivesPage />} />
          <Route path="/pos/expenses" element={<ExpenseList />} />
          <Route path="/pos/customer/:id" element={<CustomerDetails />} />
          <Route
            path="/pos/branches"
            element={isAdminPortal() ? <BranchList /> : <NotFound />}
          />
          <Route
            path="/pos/cash-denominations"
            element={<CashDenominationList />}
          />
          <Route path="/pos/cash/:id" element={<CashDenominationDetails />} />
          <Route
            path="/pos/branches/:id"
            element={isAdminPortal() ? <BranchDetails /> : <NotFound />}
          />
          <Route path="/pos/suppliers" element={<SupplierList />} />
          <Route path="/pos/supplier/:id" element={<SupplierDetails />} />
          <Route path="/pos/tags" element={<TagList />} />
          <Route path="/pos/tags/:id" element={<TagDetails />} />
          <Route path="/pos/brands" element={<BrandList />} />
          <Route path="/pos/brand/:id" element={<BrandDetails />} />
          <Route path="/pos/locations" element={<LocationList />} />
          <Route path="/pos/delivery-fees" element={<DeliveryFeeList />} />
          <Route path="/pos/attributes" element={<AttributeList />} />
          <Route path="/pos/attribute/:id" element={<AttributeDetails />} />

          <Route path="/pos/roles" element={<RoleList />} />

          <Route path="/pos/products" element={<ProductList />} />
          <Route path="/pos/branch-products" element={<BranchProductList />} />
          <Route path="/pos/product/:id" element={<ProductDetails />} />
          <Route
            path="/pos/product-variants"
            element={<ProductVariantList />}
          />
          <Route path="/pos/product-variant/:id" element={<VariantDetails />} />
          <Route path="/pos/sales" element={<OrderList />} />
          <Route path="/pos/sales-return" element={<SalesReturnList />} />
          <Route path="/pos/return-requests" element={<ReturnRequestList />} />
          <Route path="/pos/invoice/:id" element={<InvoiceDetails />} />
          <Route path="/pos/sales/:id" element={<OrderDetails />} />
          <Route
            path="/pos/website-orders/:id"
            element={<WebsiteOrderDetails />}
          />

          <Route
            path="/pos/stock-transactions"
            element={<StockTransactionList />}
          />
          <Route path="/pos/stock-audit" element={<StockAuditList />} />
          <Route
            path="/pos/stock-audit/:id"
            element={<StockAuditDetailPage />}
          />
          <Route path="/pos/damage-products" element={<DamageProductsList />} />
          <Route path="/pos/system-logs" element={<ActivityLogs />} />
          <Route
            path="/pos/stock-adjustment"
            element={<StockAdjustmentList />}
          />
          <Route
            path="/pos/stock-adjustments/:id"
            element={<AdjustmentDetails />}
          />
          <Route path="/pos/products" element={<ProductList />} />
          <Route path="/pos/product/:id" element={<ProductDetails />} />
          <Route
            path="/pos/product-variants"
            element={<ProductVariantList />}
          />
          <Route path="/pos/product-variant/:id" element={<VariantDetails />} />
          <Route path="/pos/orders" element={<OrderList />} />
          <Route path="/pos/website-orders" element={<WebsiteOrderList />} />
          <Route path="/pos/invoice/:id" element={<InvoiceDetails />} />
          <Route path="/pos/payment-methods" element={<PaymentMethodsList />} />
          <Route path="/pos/sales-status" element={<SalesStatusList />} />
          <Route path="/pos/payment-status" element={<PaymentStatusList />} />
          <Route path="/pos/reasons" element={<ReasonList />} />
          <Route path="/pos/order/:id" element={<OrderDetails />} />
          <Route path="/pos/footfall" element={<CustomerReturnList />} />
          <Route path="/pos/categories" element={<CategoryList />} />
          <Route path="/pos/category/:id" element={<CategoryDetails />} />
          <Route path="/pos/discount" element={<DiscountList />} />
          <Route path="/pos/discount/:id" element={<DiscountDetail />} />
          <Route path="/pos/exports" element={<ExportList />} />

          <Route
            path="/pos/stock-transactions"
            element={<StockTransactionList />}
          />
          <Route path="/pos/system-logs" element={<ActivityLogs />} />
          <Route
            path="/pos/stock-adjustments/:id"
            element={<AdjustmentDetails />}
          />
          <Route path="/pos/products" element={<ProductList />} />
          <Route path="/pos/product/:id" element={<ProductDetails />} />
          <Route
            path="/pos/product-variants"
            element={<ProductVariantList />}
          />
          <Route path="/pos/product-variant/:id" element={<VariantDetails />} />
          <Route path="/pos/orders" element={<OrderList />} />
          <Route path="/pos/invoice/:id" element={<InvoiceDetails />} />
          <Route path="/pos/payment-methods" element={<PaymentMethodsList />} />
          <Route path="/pos/attribute-names" element={<AttributeNameList />} />
          <Route
            path="/pos/currency-config"
            element={<DenominationSettingList />}
          />
          <Route path="/pos/note-config" element={<CurrencyNoteList />} />
          <Route path="/pos/reasons" element={<ReasonList />} />
          <Route path="/pos/order/:id" element={<OrderDetails />} />
          <Route
            path="/pos/stock-transactions"
            element={<StockTransactionList />}
          />
          <Route
            path="/pos/stock-adjustment"
            element={<StockAdjustmentList />}
          />
          <Route
            path="/pos/stock-adjustments/:id"
            element={<AdjustmentDetails />}
          />
          <Route path="/pos/purchase" element={<PurchaseList />} />

          {/* reporting paths  */}
          <Route path="/pos/sales-report" element={<SalesDashboard />} />
          <Route path="/pos/products-report" element={<ProductDashboard />} />
          <Route path="/pos/purchase" element={<PurchaseList />} />
          <Route path="/pos/purchase/:id" element={<PurchaseDetails />} />

          <Route
            path="/pos/inventory-configs"
            element={<InventoryConfigurationPage />}
          />
          <Route
            path="/pos/inventory-configs"
            element={<InventoryConfigurationPage />}
          />
          <Route path="/pos/website-config" element={<WebsiteConfig />} />
          <Route path="/pos/pos-config" element={<POSConfig />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Route>
  )
)
